import type { Dispatch, SetStateAction } from "react";
import { useRef, useState } from "react";
import {
  ChatImagePicker,
  PendingChatImage,
  pasteChatImages,
  useChatImages,
} from "@/core/components/chat-images";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { HarnessChatInput } from "@/onboarding/components/harness-chat/harness-chat-input";
import { DocumentChip } from "@/profitability/document-chip";
import { DOCUMENT_STATUS } from "@/profitability/document-constants";
import { useReportDocuments } from "@/profitability/query";
import {
  ReportDocumentsPicker,
  useReportDocumentIds,
} from "@/profitability/report-documents-picker";

/** Where a file that is not a screenshot goes in the agent chat. */
export const AGENT_IMAGE_HINT =
  "Attach the files the agent reads with Add documents.";

export const TemplateChatComposer = ({
  businessId,
  disabled,
  placeholder,
  onSend,
  draftState,
  documentState,
  imageState,
  imagesLocked = false,
  spacious = false,
}: {
  businessId: string;
  spacious?: boolean;
  draftState?: [string, Dispatch<SetStateAction<string>>];
  documentState?: [string[], Dispatch<SetStateAction<string[]>>];
  imageState?: [File[], Dispatch<SetStateAction<File[]>>];
  /** No screenshot can be added (the New agent form, while it creates the agent). */
  imagesLocked?: boolean;
  disabled: boolean;
  placeholder: string;
  onSend: (
    message: string,
    documentIds: string[],
    images: File[],
  ) => Promise<boolean>;
}) => {
  const ownDraft = useState("");
  const [draft, setDraft] = draftState ?? ownDraft;
  const ownDocuments = useState<string[]>([]);
  const [documentIds, setDocumentIds] = documentState ?? ownDocuments;
  const ownImages = useState<File[]>([]);
  const chatImages = useChatImages(imageState ?? ownImages, {
    refusedHint: AGENT_IMAGE_HINT,
  });
  const [, setImages] = imageState ?? ownImages;
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const documentsQuery = useReportDocuments(businessId, documentIds);
  const documents = documentsQuery.data ?? [];
  const picker = useReportDocumentIds(
    businessId,
    documentIds,
    documents,
    setDocumentIds,
  );
  const unreadable = documents.filter(
    (document) => document.status === DOCUMENT_STATUS.failed,
  );
  const blocked = disabled || picker.uploading || unreadable.length > 0;

  const send = async () => {
    const message = draft.trim();
    if (!message || blocked) return;
    const sent = chatImages.images;
    if (await onSend(message, documentIds, sent)) {
      setDraft("");
      setDocumentIds([]);
      // Only what went: a screenshot added while this was sending stays.
      setImages((current) => current.filter((image) => !sent.includes(image)));
    }
  };

  return (
    <div
      className="contents"
      onPaste={(event) => {
        if (!imagesLocked) pasteChatImages(event, chatImages.addFiles);
      }}
    >
      <HarnessChatInput
        draft={draft}
        disabled={blocked}
        textareaRef={textareaRef}
        onDraftChange={setDraft}
        onSend={() => void send()}
        placeholder={placeholder}
        textareaClassName={spacious ? "min-h-28" : undefined}
        header={
          <Stack align="start" gap={1} className="w-full pb-2">
            <Stack direction="row" gap={1} align="center" className="flex-wrap">
              <ReportDocumentsPicker
                uploading={picker.uploading}
                onFiles={picker.handleFiles}
              />
              <ChatImagePicker
                onFiles={chatImages.addFiles}
                disabled={imagesLocked}
              />
              {documents.map((document) => (
                <DocumentChip
                  key={document.id}
                  document={document}
                  onRemove={() => picker.removeDocument(document.id)}
                />
              ))}
            </Stack>
            {chatImages.images.length > 0 && (
              <Stack direction="row" gap={3} className="flex-wrap pt-2">
                {chatImages.images.map((image, index) => (
                  <PendingChatImage
                    key={`${image.name}-${index}`}
                    image={image}
                    onRemove={() => chatImages.removeImage(image)}
                  />
                ))}
              </Stack>
            )}
            {unreadable.length > 0 && (
              <Text variant="caption" className="text-danger">
                {unreadable.map((document) => document.file_name).join(", ")}{" "}
                could not be read. Remove it or attach a readable copy.
              </Text>
            )}
          </Stack>
        }
      />
    </div>
  );
};
